import React, { useState, useEffect, useContext } from 'react';
import { Modal, Box, SpaceBetween, Button, FormField, Select, ProgressBar, ExpandableSection } from '@amzn/awsui-components-console';
import { getUserPrivateGroups, moveDeviceBetweenGroups, fetchGroups } from './apis';
import { getCurrentAuthUserEmail, isAdmin } from '../../lib/utils';
import { RegionContext } from '../../CustomContext';
import { useNotifications } from '../../NotificationContext';

export default function MoveDeviceModal({ visible, onDismiss, devices, onMoveSuccess }) {
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [moving, setMoving] = useState(false);
    const [showAllDevices, setShowAllDevices] = useState(false);
    const { selectedRegion } = useContext(RegionContext);
    const { addSuccess, addError, addProgress, updateProgress, completeProgress, removeNotification } = useNotifications();

    useEffect(() => {
        if (visible) {
            loadAvailableGroups();
        }
    }, [visible, selectedRegion, devices]);

    const loadAvailableGroups = async () => {
        setLoading(true);
        try {
            const [adminResponse, currentUserEmail] = await Promise.all([isAdmin(), getCurrentAuthUserEmail()]);

            const isDdfAdmin = adminResponse.status || false;
            let groups = [];

            if (isDdfAdmin) {
                // DDF admin can move to any group
                const allGroupsResponse = await fetchGroups(selectedRegion.value);
                groups = allGroupsResponse.response || [];
            } else {
                // Regular user can only move to their private groups
                const privateGroupsResponse = await getUserPrivateGroups(selectedRegion.value);
                groups = (privateGroupsResponse.response || []).filter(
                    (group) => group.owner?.email === currentUserEmail
                );
            }

            const deviceGroupIds = devices?.map((device) => device.group?.id) || [];
            const filteredGroups = groups
                .filter((group) => !deviceGroupIds.includes(group.id))
                .map((group) => ({
                    label: group.name,
                    value: group.id,
                    description: `ID: ${group.id}`,
                }));

            setAvailableGroups(filteredGroups);
        } catch (err) {
            addError('Unable to retrieve available groups. Please try again.', 'Failed to load groups');
        }
        setLoading(false);
    };

    const handleMove = async () => {
        if (!selectedGroup) return;

        setMoving(true);
        const progressId = addProgress(
            <ProgressBar
                label="Moving devices"
                description="Preparing to move devices..."
                value={0}
                variant="flash"
            />,
            'Device Move Progress'
        );

        try {
            const deviceSerials = devices.map((device) => device.serial);
            
            updateProgress(progressId, 25, 
                <ProgressBar
                    label="Moving devices"
                    description="Sending move request to server..."
                    value={25}
                    variant="flash"
                />
            );

            const response = await moveDeviceBetweenGroups(deviceSerials, selectedGroup.value, selectedRegion.value);

            updateProgress(progressId, 75, 
                <ProgressBar
                    label="Moving devices"
                    description="Processing move operation..."
                    value={75}
                    variant="flash"
                />
            );

            updateProgress(progressId, 100, 
                <ProgressBar
                    label="Moving devices"
                    description="Refreshing device list..."
                    value={100}
                    variant="flash"
                />
            );

            // Check for API failure
            if (response.success === false) {
                throw new Error(response.error || 'Move operation failed');
            }

            // Handle successful response with results
            let message = '';
            let details = [];
            
            if (response.results) {
                const { moved, skipped, failed } = response.results;
                message = `Move operation completed: ${moved.count} moved`;
                if (skipped.count > 0) message += `, ${skipped.count} skipped`;
                if (failed.count > 0) message += `, ${failed.count} failed`;
                
                details = devices.map(device => 
                    `${device.deviceName || device.releasedName} (${device.serial}) → ${selectedGroup.label}`
                );
            } else {
                message = `${devices.length} device${devices.length > 1 ? 's' : ''} moved to ${selectedGroup.label}`;
                details = devices.map(device => 
                    `${device.deviceName || device.releasedName} (${device.serial}) → ${selectedGroup.label}`
                );
            }

            completeProgress(progressId, 
                <Box>
                    <Box>{message}</Box>
                    <ExpandableSection headerText="View details">
                        <SpaceBetween size="xs">
                            {details.map((detail, index) => (
                                <Box key={index} fontSize="body-s">{detail}</Box>
                            ))}
                        </SpaceBetween>
                    </ExpandableSection>
                </Box>,
                details
            );

            onMoveSuccess();
            onDismiss();
        } catch (err) {
            // Handle error responses
            const errorData = err.response?.data;
            const errorMessage = errorData?.errorCode || err.message || 'Failed to move devices. Please try again.';
            const errorHeader = `Error ${errorData?.statusCode || ''}`;

            // Convert progress to error
            removeNotification(progressId);
            addError(errorMessage, errorHeader);
        }
        setMoving(false);
    };

    const handleDismiss = () => {
        setSelectedGroup(null);
        setShowAllDevices(false);
        onDismiss();
    };

    return (
        <Modal
            onDismiss={handleDismiss}
            visible={visible}
            closeAriaLabel="Close modal"
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleDismiss}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleMove}
                            disabled={!selectedGroup || moving}
                            loading={moving}
                        >
                            Move {devices?.length === 1 ? 'Device' : 'Devices'}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            header="Move Devices Between Groups"
        >
            <SpaceBetween size="m">
                <Box>
                    <Box variant="h3">Selected Devices ({devices?.length || 0})</Box>
                    {(showAllDevices ? devices : devices?.slice(0, 3))?.map((device) => (
                        <Box key={device.serial}>
                            <strong>{device.deviceName || device.releasedName}</strong> ({device.serial}) -{' '}
                            {device.group?.name}
                        </Box>
                    ))}
                    {devices?.length > 3 && !showAllDevices && (
                        <Box>
                            <Button variant="link" onClick={() => setShowAllDevices(true)}>
                                ... and {devices.length - 3} more devices
                            </Button>
                        </Box>
                    )}
                    {showAllDevices && devices?.length > 3 && (
                        <Box>
                            <Button variant="link" onClick={() => setShowAllDevices(false)}>
                                Show less
                            </Button>
                        </Box>
                    )}
                </Box>

                <FormField label="Target Group">
                    <Select
                        selectedOption={selectedGroup}
                        onChange={({ detail }) => setSelectedGroup(detail.selectedOption)}
                        options={availableGroups}
                        placeholder="Choose a target group"
                        loading={loading}
                        empty="No other groups available"
                    />
                </FormField>
            </SpaceBetween>
        </Modal>
    );
}
